import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {RecoveryTokens} from '~shared/schema/wiki/actions/recovery-tokens';

import {
    BasePanelState,
    init,
    loadNextPage,
    makeAddItems,
    makeChangeOrder,
    makeReset,
    makeSetItems,
    setError,
    setLoading,
    setLoadingMore,
    setQueryFilter,
} from '../common';

import {recoverPageByToken, recoveryTokenDelete} from './thunks';
import {RecoveryTokensTypeFilter} from './types';

export type RecoveryTokensState = BasePanelState<
    RecoveryTokensItem,
    {type: RecoveryTokensTypeFilter}
>;
export type RecoveryTokensItem = ReturnType<typeof toTableData>;

const DEFAULT_ORDER_BY = 'pageExpiresAt';

const initialState: RecoveryTokensState = {
    items: [],
    isFirstPage: true,
    nextCursor: null,
    loading: false,
    loadingMore: false,
    isEmpty: true,
    filters: {
        query: '',
        type: RecoveryTokensTypeFilter.UserPage,
        userFilter: [],
    },
    orderBy: DEFAULT_ORDER_BY,
    orderDirection: 'desc',
};

export const {actions, reducer} = createSlice({
    name: 'recoveryTokens',
    initialState,
    reducers: {
        init,
        reset: makeReset(initialState),
        loadNextPage,
        changeOrder: makeChangeOrder(DEFAULT_ORDER_BY),
        setItems: makeSetItems(toTableData),
        addItems: makeAddItems(toTableData),
        setLoading,
        setLoadingMore,
        setError,
        setQueryFilter,

        setTypeFilter(state, action: PayloadAction<RecoveryTokensTypeFilter>) {
            state.nextCursor = null;
            state.isFirstPage = true;
            state.filters.type = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(recoverPageByToken.pending, (state, {meta}) => {
                const deleteItemId = meta.arg.recoveryTokenId;
                const item = state.items.find((item) => item.id === deleteItemId);
                if (!item) return;

                item.isRemoved = true;
            })
            .addCase(recoverPageByToken.fulfilled, (state) => {
                state.items = state.items.filter((item) => {
                    if (item.isRemoved && !item.isActive) {
                        return false;
                    }
                    return true;
                });
            })
            .addCase(recoverPageByToken.rejected, (state, {meta}) => {
                const deleteItemId = meta.arg.recoveryTokenId;
                const item = state.items.find((item) => item.id === deleteItemId);

                if (!item) return;

                item.isRemoved = false;
            })
            .addCase(recoveryTokenDelete.pending, (state, {meta}) => {
                const deleteItemId = meta.arg.recoveryTokenId;
                const item = state.items.find((item) => item.id === deleteItemId);

                if (!item) return;

                item.isRemoved = true;
            })
            .addCase(recoveryTokenDelete.fulfilled, (state) => {
                state.items = state.items.filter((item) => {
                    if (item.isRemoved && !item.isActive) {
                        return false;
                    }
                    return true;
                });
            })
            .addCase(recoveryTokenDelete.rejected, (state, {meta}) => {
                const deleteItemId = meta.arg.recoveryTokenId;
                const item = state.items.find((item) => item.id === deleteItemId);

                if (!item) return;

                item.isRemoved = false;
            });
    },
});

function toTableData(bookmark: RecoveryTokens) {
    return {
        id: bookmark.id,
        author: bookmark.user,
        title: bookmark.page.title,
        slug: bookmark.page.slug,
        pageExpiresAt: bookmark.expires_at,
        createdAt: bookmark.created_at,
        isActive: bookmark.page.is_active,
        isRemoved: false,
        actions: bookmark.page,
    };
}
